import { type Editor } from '@tiptap/react'

// Smart paste-to-embed. When a bare URL from a known provider is pasted, it
// auto-expands into the right block instead of a plain link. Add a provider
// here to support a new domain (Vimeo, Loom, CodePen…) — each one tests the
// URL and runs the command that inserts its node.
export interface LinkProvider {
  name: string
  test: (url: URL) => boolean
  apply: (editor: Editor, rawUrl: string) => boolean
}

export const LINK_PROVIDERS: LinkProvider[] = [
  {
    name: 'youtube',
    test: url =>
      /(^|\.)youtube\.com$/.test(url.hostname) ||
      /(^|\.)youtube-nocookie\.com$/.test(url.hostname) ||
      url.hostname === 'youtu.be',
    apply: (editor, rawUrl) => editor.chain().focus().setYoutubeVideo({ src: rawUrl }).run(),
  },
  {
    name: 'loom',
    test: url => /(^|\.)loom\.com$/.test(url.hostname),
    apply: (editor, rawUrl) => editor.chain().focus().setLoomVideo({ src: rawUrl }).run(),
  },
]

// Returns true if the text was a bare provider URL and got expanded (so the
// paste handler should swallow the event). Anything else falls through to the
// editor's normal paste (plain link / text).
export function expandPastedUrl(editor: Editor, text: string): boolean {
  const trimmed = text.trim()
  if (!/^https?:\/\/\S+$/i.test(trimmed)) return false
  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    return false
  }
  const provider = LINK_PROVIDERS.find(p => p.test(url))
  return provider ? provider.apply(editor, trimmed) : false
}
